import React, { FC } from 'react';
import { IconButton, InputBase, Stack } from '@mui/material';
import { themePalette } from '@theme/muiTheme';
import { CloseCircleIcon, SearchIcon } from '@utils/iconsDefs';

export const CIS_EXIT_SEARCH_LABEL = 'Exit search';

/**
 * AC5.1.1's placeholder, naming the catalogue's size — Controls and
 * Safeguards combined.
 */
export const cisSearchPlaceholder = (total: number): string =>
  `Search ${total} references (e.g. 8.5 or firewall)`;

type Props = {
  query: string;
  total: number;
  onChange: (query: string) => void;
  onExit: () => void;
};

/**
 * The search field that takes the pill row's place in the CIS-18 reference
 * panel (AC5.1): a bordered input led by the search glyph and ended by the
 * ⓧ that leaves search (AC5.5.1).
 *
 * It focuses itself when it appears, since a click on the search icon is
 * what brings it up, and reports every keystroke as it is — the list
 * filters from the first character, with no debounce and no minimum
 * length (AC5.2), which is why it does not share the entities list's
 * search store and its three-character threshold. The shape follows that
 * list's search header, at the pill row's 52px height so the list below
 * does not shift when search opens.
 */
const FilterCisSearchField: FC<Props> = ({
  query,
  total,
  onChange,
  onExit,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{ height: 52, px: '12px', flexShrink: 0 }}
  >
    <Stack
      direction="row"
      alignItems="center"
      gap="8px"
      flex={1}
      minWidth={0}
      sx={{
        height: 36,
        px: '12px',
        borderRadius: '12px',
        border: `1px solid ${themePalette.grey[700]}`,
        '&:focus-within': {
          border: `2px solid ${themePalette.primary.main}`,
        },
      }}
    >
      <SearchIcon width={20} height={20} style={{ flexShrink: 0 }} />

      <InputBase
        autoFocus
        fullWidth
        value={query}
        placeholder={cisSearchPlaceholder(total)}
        inputProps={{ 'aria-label': 'Search references' }}
        onChange={(event) => onChange(event.target.value)}
        sx={{ fontSize: 14, color: themePalette.grey[1000] }}
      />

      <IconButton
        size="small"
        onClick={onExit}
        aria-label={CIS_EXIT_SEARCH_LABEL}
        sx={{ p: 0 }}
      >
        <CloseCircleIcon width={20} height={20} style={{ flexShrink: 0 }} />
      </IconButton>
    </Stack>
  </Stack>
);

export default FilterCisSearchField;
